<template>
  <div>
    <!-- Modo de visualización mini -->
    <AlertsMini
      v-if="isMini"
      :copp-id="coppId"
      :alertas="alertas"
      :filter-by-fecha="props.filterByFecha"
      :show-title="props.showTitle"
      :show-close-button="props.showCloseButton"
      :cmot-tipo="props.cmotTipo"
      :citt-id-context="props.cittIdContext"
      @alerta-cerrada="handleAlertaCerrada"
    >
      <template #actions>
        <ButtonDefault
          v-if="showAddButton"
          variant="outline"
          size="xs"
          @click="openAlertsModal"
        >
          <PlusIcon class="shrink-0 w-3 h-3" />
          <span class="xs:hidden lg:block">Agregar alerta</span>
        </ButtonDefault>
      </template>
    </AlertsMini>

    <!-- Modo de visualización completa -->
    <AlertsFull
      v-else-if="!isMini"
      :copp-id="coppId"
      :alertas="alertas"
      :filter-by-fecha="props.filterByFecha"
      :show-title="props.showTitle"
      :show-close-button="props.showCloseButton"
      :cmot-tipo="props.cmotTipo"
      :citt-id-context="props.cittIdContext"
      @alerta-cerrada="handleAlertaCerrada"
    >
      <template #actions>
        <ButtonDefault
          v-if="showAddButton"
          variant="outline"
          size="xs"
          @click="openAlertsModal"
        >
          <PlusIcon class="shrink-0 w-3 h-3" />
          <span class="xs:hidden lg:block">Agregar alerta</span>
        </ButtonDefault>
      </template>
    </AlertsFull>

    <!-- Modo de gestión de alertas (modal) -->
    <AlertsModal
      v-if="modalMounted"
      :copp-id="coppId"
      :alertas="alertas"
      @alerta-creada="handleAlertaCreada"
      @update="handleUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { PlusIcon } from '@heroicons/vue/24/outline'
import { openModal } from '@/modules/Core/usings/generales.js'
import { useAlerts } from '../composables/useAlerts'
import { nextTick, ref, toRef } from 'vue'
import AlertsMini from './Alertas/AlertsMini.vue'
import AlertsFull from './Alertas/AlertsFull.vue'
import AlertsModal from './Alertas/AlertsModal.vue'

interface Props {
  coppId: number
  isMini?: boolean
  filterByFecha?: boolean
  isPrivate?: boolean
  showTitle?: boolean
  showAddButton?: boolean
  showCloseButton?: boolean
  cmotTipo?: string
  cittIdContext?: number
}

interface Emits {
  (e: 'alerta-creada'): void
  (e: 'update'): void
}

const props = withDefaults(defineProps<Props>(), {
  isMini: false,
  filterByFecha: true,
  isPrivate: false,
  showTitle: true,
  showAddButton: false,
  showCloseButton: false,
  cmotTipo: 'CIERRE ALERTA',
  cittIdContext: undefined
})
const emit = defineEmits<Emits>()
const modalMounted = ref(false)

const {
  alertas,
  loadAlertas,
  reloadAlertas,
  setupWatchers
} = useAlerts({
  coppId: toRef(props, 'coppId'),
  emit: (event: string) => {
    if (event === 'alerta-creada') {
      emit('alerta-creada')
    } else if (event === 'update') {
      emit('update')
    }
  }
})

loadAlertas()

setupWatchers()

const openAlertsModal = async () => {
  modalMounted.value = true
  await nextTick()
  openModal('modal_alertas', false)
}

const handleAlertaCreada = async () => {
  try {
    await loadAlertas()
  } catch (e) {
    console.error('Error recargando alertas tras creación:', e)
  }
  emit('alerta-creada')
}

const handleUpdate = async () => {
  try {
    await reloadAlertas()
  } catch (e) {
    console.error('Error recargando alertas tras update:', e)
  }
  emit('update')
}

const handleAlertaCerrada = async () => {
  try {
    await reloadAlertas()
  } catch (e) {
    console.error('Error recargando alertas tras cierre:', e)
  }
  emit('update')
}

// Exponer métodos para uso externo
defineExpose({
  loadAlertas,
  reloadAlertas,
  openAlertsModal
})
</script>
